import { ApolloError, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { readOAuthProviderConfigurationSchemaQuery } from 'apis/partnerIntegrations/oAuth/partnerIntegrationsOAuth.queries';
import {
  OAuthProvider,
  OAuthStrategySchemaField,
} from 'apis/partnerIntegrations/oAuth/types/partnerIntegrationsOAuth.types';
import {
  OAuthStrategySchemaArg,
  OAuthStrategySchemaResponse,
} from 'apis/partnerIntegrations/oAuth/types/partnerIntegrationsOAuthApi.types';

import type {
  OAuthProviderConfigurationFormValues,
  OAuthProviderConfigurationMode,
} from '../oauthProviderConfigurationModal.types';

type UseOAuthProviderConfigurationFormArg = {
  mode: OAuthProviderConfigurationMode;
  strategyId?: string;
  provider?: OAuthProvider;
};

type UseOAuthProviderConfigurationFormResponse = {
  schema: OAuthStrategySchemaField[];
  values: OAuthProviderConfigurationFormValues;
  isSchemaLoading: boolean;
  schemaError?: ApolloError;
};

type BuildFormValuesArg = {
  schema: OAuthStrategySchemaField[];
  provider?: OAuthProvider;
  isCreateMode: boolean;
};

const buildFormValues = ({
  schema,
  provider,
  isCreateMode,
}: BuildFormValuesArg): OAuthProviderConfigurationFormValues => {
  const settings = schema.reduce<OAuthProviderConfigurationFormValues>(
    (acc, field) => ({
      ...acc,
      [field.key]: {
        key: field.key,
        secret: field.secret,
        value:
          provider?.settings.find((setting) => setting.key === field.key)
            ?.value ||
          field.defaultValue ||
          '',
        // Show editing UI for create mode or non-secret fields in update mode
        isEditing: isCreateMode || !field.secret,
      },
    }),
    {},
  );

  return {
    ...settings,
    name: { key: 'name', secret: false, value: provider?.name ?? '' },
  };
};

export const useOAuthProviderConfigurationForm = ({
  mode,
  strategyId,
  provider,
}: UseOAuthProviderConfigurationFormArg): UseOAuthProviderConfigurationFormResponse => {
  const {
    data,
    loading: isSchemaLoading,
    error: schemaError,
  } = useQuery<OAuthStrategySchemaResponse, OAuthStrategySchemaArg>(
    readOAuthProviderConfigurationSchemaQuery,
    {
      skip: !strategyId,
      variables: { id: strategyId ?? '' },
    },
  );

  const schema = useMemo(() => {
    const fields = data?.Management.oAuthProviderConfigurationSchema ?? [];

    return [...fields].sort(({ required: a }, { required: b }) => +b - +a);
  }, [data]);

  const values = useMemo(
    () =>
      buildFormValues({ schema, provider, isCreateMode: mode === 'create' }),
    [schema, provider, mode],
  );

  return { schema, values, isSchemaLoading, schemaError };
};
